import find from 'lodash/find';
import keys from 'lodash/keys';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

const APP_NAME = 'HiLo Analytics';

// Browser tab title per route, keyed by react-router path pattern. Labels
// match the sidebar so the tab reads like the nav item. Pages with a record
// name (a dashboard, a chat, ...) override these via useDocumentTitle once
// the record loads. Internal/test routes are left out on purpose and fall
// back to the bare app name.
const ROUTE_TITLES: Record<string, string> = {
  // The homepage renders the watchlists component (routes/index.tsx)
  '/': 'Watchlists',
  '/dashboard': 'Dashboards',
  '/dashboard/:id': 'Dashboards',
  '/dashboard/:id/:slug': 'Dashboards',
  '/watchlists': 'Watchlists',
  '/watchlists/:wlId': 'Watchlists',
  '/watchlists/:wlId/trades/:tradeId': 'Watchlists',
  '/fundamentals-summary': 'Fundamentals Summary',
  '/cot': 'Commitment of Traders',
  '/open-interest': 'Open Interest',
  '/global-flows': 'Global Flows',
  '/global-margins': 'Global Margins',
  '/geopolitics': 'Geopolitics',
  '/petrologistics-shiptracking': 'Petro-Logistics Shiptracking',
  '/tanker-data-hub': 'Tanker Data Hub',
  '/daily-reports': 'Daily Reports',
  '/daily-reports/:id': 'Daily Report',
  '/chat': 'HiLo Agent',
  '/chat/:id': 'HiLo Agent',
  '/scheduled-tasks': 'Scheduled Tasks',
  '/customize': 'Customize',
  '/data-api-docs': 'Data API Docs',
  '/shared/chats/:token': 'Shared chat',
  '/sign-in': 'Sign in',
  '/sign-up': 'Sign up',
};

// Patterns are matched exactly (react-router semantics) rather than by
// prefix, so `/dashboard` never claims `/dashboards-foo`.
const routeTitle = (pathname: string): string => {
  const pattern = find(keys(ROUTE_TITLES), (path) =>
    Boolean(matchPath(pathname, { path, exact: true })),
  );
  return pattern ? ROUTE_TITLES[pattern] : '';
};

type SetOverride = (title: string | null) => void;

const DocumentTitleContext = createContext<SetOverride | null>(null);

// Owns `document.title` for the whole app. The default comes from the route;
// a mounted page can replace it with a record name through useDocumentTitle.
// Resolving both here, rather than letting pages and the layout each write
// document.title, avoids effect-ordering races: child effects run before
// parent effects, so a layout-level default would clobber a page's title on
// the same commit (e.g. a back-nav to a cached chat).
export function DocumentTitleProvider ({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const [override, setOverride] = useState<string | null>(null);
  const title = override || routeTitle(pathname);

  useEffect(() => {
    document.title = title ? `${APP_NAME} | ${title}` : APP_NAME;
  }, [title]);

  return (
    <DocumentTitleContext.Provider value={setOverride}>
      {children}
    </DocumentTitleContext.Provider>
  );
}

// Replaces the route's tab title with `title` while the calling page is
// mounted. An empty/undefined title (record still loading) keeps the route
// default, and unmounting restores it.
export function useDocumentTitle (title?: string | null) {
  const setOverride = useContext(DocumentTitleContext);
  useEffect(() => {
    if (!setOverride) return undefined;
    setOverride(title || null);
    return () => setOverride(null);
  }, [setOverride, title]);
}
